import { SYSTEM_PRESETS } from '../constants/presets';

/**
 * PresetStorage
 *
 * Gerencia persistencia de presets usando LocalStorage como storage primario.
 * Futuramente sera integrado com backend API.
 */
class PresetStorage {
    constructor() {
        this.STORAGE_KEY = 'sellout_presets';
        this.ACTIVE_PRESET_KEY = 'sellout_active_preset';
    }

    /**
     * Carrega todos os presets (sistema + customizados)
     * @returns {Promise<{system: Array, custom: Array}>}
     */
    async fetchPresets() {
        try {
            const customPresets = this.loadFromLocal();

            return {
                system: SYSTEM_PRESETS,
                custom: customPresets,
            };
        } catch (error) {
            console.error('Erro ao carregar presets:', error);
            return {
                system: SYSTEM_PRESETS,
                custom: [],
            };
        }
    }

    /**
     * Salva um novo preset customizado
     * @param {Object} preset - Preset a ser salvo
     * @returns {Promise<Object>} Preset salvo com ID e timestamps
     */
    async savePreset(preset) {
        try {
            const customPresets = this.loadFromLocal();

            // Gerar ID unico
            const id = `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            const newPreset = {
                ...preset,
                id,
                type: 'custom',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };

            customPresets.push(newPreset);
            this.saveToLocal(customPresets);

            return newPreset;
        } catch (error) {
            console.error('Erro ao salvar preset:', error);
            throw new Error('Falha ao salvar preset');
        }
    }

    /**
     * Atualiza um preset customizado existente
     * @param {string} id - ID do preset
     * @param {Object} updates - Atualizacoes parciais
     * @returns {Promise<Object>} Preset atualizado
     */
    async updatePreset(id, updates) {
        try {
            const customPresets = this.loadFromLocal();
            const index = customPresets.findIndex((p) => p.id === id);

            if (index === -1) {
                throw new Error('Preset nao encontrado');
            }

            customPresets[index] = {
                ...customPresets[index],
                ...updates,
                updatedAt: new Date().toISOString(),
            };

            this.saveToLocal(customPresets);

            return customPresets[index];
        } catch (error) {
            console.error('Erro ao atualizar preset:', error);
            throw new Error('Falha ao atualizar preset');
        }
    }

    /**
     * Exclui um preset customizado
     * @param {string} id - ID do preset
     * @returns {Promise<void>}
     */
    async deletePreset(id) {
        try {
            const customPresets = this.loadFromLocal();
            const filtered = customPresets.filter((p) => p.id !== id);

            this.saveToLocal(filtered);
        } catch (error) {
            console.error('Erro ao excluir preset:', error);
            throw new Error('Falha ao excluir preset');
        }
    }

    /**
     * Obtem o ID do preset ativo
     * @returns {string|null}
     */
    getActivePresetId() {
        try {
            return localStorage.getItem(this.ACTIVE_PRESET_KEY);
        } catch (error) {
            console.error('Erro ao obter preset ativo:', error);
            return null;
        }
    }

    /**
     * Define o preset ativo
     * @param {string} presetId - ID do preset
     */
    setActivePresetId(presetId) {
        try {
            localStorage.setItem(this.ACTIVE_PRESET_KEY, presetId);
        } catch (error) {
            console.error('Erro ao definir preset ativo:', error);
        }
    }

    /**
     * Salva presets no LocalStorage
     * @param {Array} presets - Array de presets customizados
     */
    saveToLocal(presets) {
        try {
            const data = JSON.stringify(presets);
            localStorage.setItem(this.STORAGE_KEY, data);
        } catch (error) {
            console.error('Erro ao salvar no LocalStorage:', error);
            throw error;
        }
    }

    /**
     * Carrega presets do LocalStorage
     * @returns {Array} Array de presets customizados
     */
    loadFromLocal() {
        try {
            const data = localStorage.getItem(this.STORAGE_KEY);

            if (!data) {
                return [];
            }

            const presets = JSON.parse(data);

            // Validar estrutura basica
            if (!Array.isArray(presets)) {
                console.warn('Dados invalidos no LocalStorage, resetando');
                return [];
            }

            return presets;
        } catch (error) {
            console.error('Erro ao carregar do LocalStorage:', error);
            return [];
        }
    }

    /**
     * Limpa todos os presets customizados (util para testes)
     */
    clearCustomPresets() {
        try {
            localStorage.removeItem(this.STORAGE_KEY);
            localStorage.removeItem(this.ACTIVE_PRESET_KEY);
        } catch (error) {
            console.error('Erro ao limpar presets:', error);
        }
    }
}

export default PresetStorage;
